import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {ArrowClockwise28Filled, Settings28Regular} from "@fluentui/react-icons";
import {Badge, Button, makeStyles, SearchBox, Spinner, Text, tokens} from "@fluentui/react-components";
import {useRouter} from "../Router";
import {useTemplateStore} from "../stores/templateStore";
import ErrorView from "../components/ErrorView";
import {BusinessUnit} from "../../types/types";
import TemplateItem from "../components/TemplateItem";
import BusinessUnitDropdown from "../components/BusinessUnitDropdown";
import {getDefaultBusinessUnit, setDefaultBusinessUnit} from "../../util";
import Page from "../components/Page";

const useStyles = makeStyles({
    taskBar: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingBottom: tokens.spacingVerticalS,
        gap: tokens.spacingHorizontalS
    },
    scrollable: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    centerAlign: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    unitFilter: {
        marginBottom: tokens.spacingVerticalS,
    },
    businessUnit: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
        width: '100%',
    },
    list: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    card: {
        cursor: 'pointer',
        borderLeftWidth: '4px',
        borderLeftStyle: 'solid',
    },
    description: {
        color: tokens.colorNeutralForeground3,
    },
    tags: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacingHorizontalXS,
        marginTop: tokens.spacingVerticalXS,
    },
    empty: {
        color: tokens.colorNeutralForeground3,
    }
});

export default function TemplateListPage() {
    const styles = useStyles();
    const {t} = useTranslation();
    const {setPage} = useRouter();
    const {items, isLoading, error, fetchItems} = useTemplateStore();

    const [search, setSearch] = useState('');
    const [selectedUnit, setSelectedUnit] = useState<BusinessUnit | null>(getDefaultBusinessUnit());
    const [selectedTags, setSelectedTags] = useState<string[]>([]);

    useEffect(() => {
        if (!items) void fetchItems();
    }, []);

    const allTags = Array.from(new Set(items?.flatMap(item => item.tags ?? []) ?? [])).sort((a, b) => a.localeCompare(b));
    const itemsFiltered = items?.sort((a, b) => a.title.localeCompare(b.title)).filter(item => {
        const matchesSearch = item.title.toLowerCase().includes(search.toLowerCase());
        const matchesUnit = selectedUnit === null || item.businessUnit === selectedUnit;
        const matchesTags = selectedTags.length === 0 || selectedTags.every(tag => item.tags?.includes(tag));
        return matchesSearch && matchesUnit && matchesTags;
    });

    function loadTemplateItems() {
        setSelectedTags([]);
        void fetchItems();
    }

    return (
        <Page>
            <div className={styles.taskBar}>
                <Text size={400} weight="semibold" style={{flex: 1}}>
                    {items ? items.length : ''} {t("translation:templates.title")}
                </Text>
                <Button
                    icon={<ArrowClockwise28Filled/>}
                    appearance="subtle"
                    onClick={loadTemplateItems}
                    disabled={isLoading}
                    aria-label="Refresh"
                />
                <Button
                    icon={<Settings28Regular/>}
                    appearance="subtle"
                    onClick={() => setPage({kind: 'settings'})}
                    aria-label="Settings"
                />
            </div>

            <SearchBox
                placeholder={t("translation:general.search")}
                value={search}
                onChange={(_, data) => setSearch(data.value)}
                style={{width: '100%', marginBottom: tokens.spacingVerticalS}}
            />

            <div style={{paddingBottom: tokens.spacingVerticalS}}>
                <BusinessUnitDropdown
                    value={selectedUnit ?? 'ALL'}
                    onChange={(value) => {
                        setSelectedUnit(value === 'ALL' ? null : value);
                        setDefaultBusinessUnit(value === 'ALL' ? null : value);
                    }}
                />
            </div>

            {allTags.length > 0 &&
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: tokens.spacingHorizontalXS,
                    marginBottom: tokens.spacingVerticalS,
                }}>
                    {allTags.map(tag => (
                        <Badge
                            key={tag}
                            appearance={selectedTags.includes(tag) ? 'filled' : 'outline'}
                            color={selectedTags.includes(tag) ? 'brand' : 'informative'}
                            size="medium"
                            style={{cursor: 'pointer', borderRadius: '4px'}}
                            onClick={() => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag])}
                        >
                            {tag}
                        </Badge>
                    ))}
                </div>
            }

            <div className={styles.scrollable}>
                {isLoading &&
                    <div className={styles.centerAlign}>
                        <Spinner size="medium"/>
                    </div>
                }

                {error &&
                    <div className={styles.centerAlign}>
                        <ErrorView error={error} onRetry={loadTemplateItems}/>
                    </div>
                }

                {itemsFiltered?.length === 0 &&
                    <div className={styles.centerAlign}>
                        <Text>{t("translation:templates.empty")}</Text>
                    </div>
                }

                {itemsFiltered &&
                    <div className={styles.list}>
                        {itemsFiltered.map((item) => (
                            <TemplateItem key={`template_item_${item.id}`}
                                          id={item.id}
                                          title={item.title}
                                          businessUnit={item.businessUnit}
                                          description={item.description}
                                          tags={item.tags}
                                          onClick={() => setPage({kind: 'detail', templateId: item.id})}/>
                        ))}
                    </div>
                }
            </div>
        </Page>
    );
}

